import Controller from '@ember/controller';
import { action, get } from '@ember/object';
import { service } from '@ember/service';
import { isPresent, isNone } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { addMinutes, isFuture, endOfDay, startOfDay, subDays, format, formatISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { timeout, dropTask, restartableTask } from 'ember-concurrency';
import config from 'garaje/config/environment';
import type AccessEventModel from 'garaje/models/access-event';
import type { FilterItem } from 'garaje/models/common';
import type { Field } from 'garaje/models/location';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type CurrentAdminService from 'garaje/services/current-admin';
import type CurrentLocationService from 'garaje/services/current-location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type LocalStorageService from 'garaje/services/local-storage';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type StoreService from 'garaje/services/store';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import { saveColumnPreferences } from 'garaje/utils/column-preferences';
import { modifyDateInTimeZone, parseYyyyMmDdInTimeZone, DATE_FNS_YYYY_MM_DD } from 'garaje/utils/date-fns-tz-utilities';
import urlBuilder from 'garaje/utils/url-builder';
import zft from 'garaje/utils/zero-for-tests';
import { and, notEmpty, reads } from 'macro-decorators';

import type { WorkplaceAccessEventsRouteModel } from './route';

const MIN_SEARCH_LENGTH = 3;

const HEADER_ENUMS = {
  name: 'Name',
  autoSignInStatus: 'Auto sign-in status',
  entranceName: 'Entrance Name',
  integrationName: 'Integration Name',
  eventTimeHeader: 'Event Time',
  receivedAtHeader: 'Received at',
  date: 'Date',
  eventType: 'Event Type',
  outcome: 'Outcome',
};

const PRIMARY_HEADERS = [
  {
    name: HEADER_ENUMS.name,
    componentName: 'custom-column',
    sort: 'person-name',
    tooltip: 'Person who triggered the access event',
  },
  {
    name: HEADER_ENUMS.eventTimeHeader,
    componentName: 'custom-column',
    sort: 'event-timestamp',
    tooltip: 'When the access event actually occurred',
    tooltipPosition: 'bottom',
  },
  {
    name: HEADER_ENUMS.receivedAtHeader,
    componentName: 'custom-column',
    sort: 'received-at-timestamp',
    tooltip: 'When event was received by our services',
    tooltipPosition: 'top',
  },
  {
    name: HEADER_ENUMS.integrationName,
    componentName: 'custom-column',
    tooltip: 'Integration that reported the event',
    tooltipPosition: 'bottom',
  },
  { name: HEADER_ENUMS.entranceName, componentName: 'custom-column', tooltip: 'Door or entry point that was accessed' },
  {
    name: HEADER_ENUMS.eventType,
    componentName: 'custom-column',
    tooltip: 'Type of access event',
    tooltipPosition: 'top',
  },
  {
    name: HEADER_ENUMS.outcome,
    componentName: 'custom-column',
    tooltip: 'Whether the event resulted in a sign in, failure, or no change',
    tooltipPosition: 'top',
  },
];

const OUTCOME_FILTERS: FilterItem[] = [
  { name: 'Signed in', filter: 'SIGNED_IN_SUCCESSFULLY' },
  { name: 'Failure', filter: 'FAILED' },
  { name: 'No change', filter: 'NOT_PROCESSABLE' },
];

const ACTIVITY_TYPES: FilterItem[] = [
  {
    name: 'All events',
    filter: 'ALL',
    show: true,
    tooltipPosition: 'bottom',
    tooltip: 'All access events',
  },
  {
    name: 'Sign-Ins',
    filter: 'SIGN_INS',
    show: true,
    tooltipPosition: 'top',
    tooltip: 'Access events that resulted in a Sign-in',
  },
];

interface SortableField extends Field {
  sort?: string;
  tooltip?: string;
  tooltipPosition?: string;
}

interface PluginStatusResponse {
  jsonapi: {
    version: string;
  };
  data: {
    type: string;
    id: string;
    relationships: {
      'plugin-install': {
        data: {
          id: string;
          type: string;
        };
      };
      location: {
        data: {
          id: string;
          type: string;
        };
      };
    };
    attributes: {
      'last-successful-event'?: string | null;
      'current-status': 'OK' | 'FAILURE' | 'UNKNOWN' | 'MISSING' | 'WARNING';
      'integration-name': string;
    };
  }[];
}

type PluginInstallIdentifier = string;

enum Status {
  OK = 'OK',
  FAILURE = 'FAILURE',
  UNKNOWN = 'UNKNOWN',
  MISSING = 'MISSING',
  WARNING = 'WARNING',
}
interface BasePluginState {
  status: Status;
  lastSuccessfulEvent?: string | null; // ISO string for OffsetDateTime (optional - missing for Indeterminate state)
}

export interface PluginInstallStatus {
  installId: PluginInstallIdentifier;
  locationId: string | null;
  zoneId: string | null;
  currentStatus: BasePluginState;
  isDismissed: boolean;
  integrationName: string;
}

export default class WorkplaceAccessEventsController extends Controller {
  declare model: WorkplaceAccessEventsRouteModel;
  declare limit: number;

  @service declare currentAdmin: CurrentAdminService;
  @service declare currentLocation: CurrentLocationService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare localStorage: LocalStorageService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare workplaceMetrics: WorkplaceMetricsService;
  @service declare ajax: AjaxFetchService;

  queryParams = [
    'startDate',
    'endDate',
    'date',
    'selectedDateRange',
    'query',
    'filter',
    'refresh',
    'sort',
    'activityType',
    'integrations',
    'outcomes',
  ];
  private calendarRef: HTMLElement | null = null;
  @tracked dashboardFieldsByLocationId: Record<string, Field[]> = {};

  @tracked refresh = ''; // Flag to force a model refresh
  @tracked date: string | null = '';

  @tracked inputQuery: string | null = null;
  @tracked filter = 'all';

  // query params
  @tracked query = '';
  @tracked startDate: Date | null = startOfDay(new Date());
  @tracked endDate: Date | null = endOfDay(new Date());
  @tracked reset: boolean = false;

  @tracked startTime: Date | null = startOfDay(new Date());
  @tracked endTime: Date | null = endOfDay(new Date());

  @tracked totalAccessEvents: number | null = null;
  @tracked showCalendar = false;
  @tracked selectedDateRange = '';
  @tracked pluginStatuses: PluginInstallStatus[] = [];

  @tracked currentActivity = 'ALL';
  @tracked selectedOutcomes: FilterItem[] = [];
  @tracked selectedIntegrationNames: string[] = [];
  @tracked _observedIntegrationNames: Set<string> = new Set();

  @action
  setTotalAccessEvents(total: number): void {
    this.totalAccessEvents = total;
  }

  get usePersonGrouping(): boolean {
    return this.featureFlags.isEnabled('access-events-log-person-grouping');
  }

  exportCsvTask = dropTask(async () => {
    const { currentLocation } = this.state;
    const baseUrl = `${config.badgeLogApiHost}/a/badge-log/v1/person-access-event-groups`;

    const filterParams: Record<string, string> = {
      'filter[location-ids]': currentLocation.id,
      'filter[start-time]': formatISO(this.startDateWithDefault),
      'filter[end-time]': formatISO(this.endDateWithDefault),
    };

    if (isPresent(this.query)) {
      filterParams['filter[query]'] = this.query;
    }
    if (this.selectedOutcomes.length > 0) {
      filterParams['filter[outcomes]'] = this.selectedOutcomes.map((o) => o.filter).join(',');
    }
    if (this.selectedIntegrationNames.length > 0) {
      filterParams['filter[integration-names]'] = this.selectedIntegrationNames.join(',');
    }

    const url = `${baseUrl}?${new URLSearchParams(filterParams).toString()}`;
    const { response } = await this.ajax.raw(url, {
      headers: { Accept: 'text/csv' },
    });

    const csvText = await response.text();
    const blob = new Blob([csvText], { type: 'text/csv' });
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = 'access-events.csv';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(downloadUrl);
  });

  mapStatus(status: string): Status {
    switch (status) {
      case 'OK':
        return Status.OK;
      case 'FAILURE':
        return Status.FAILURE;
      case 'UNKNOWN':
        return Status.UNKNOWN;
      case 'MISSING':
        return Status.MISSING;
      case 'WARNING':
        return Status.WARNING;
      default:
        return Status.FAILURE; // Default to FAILURE for any unrecognized status
    }
  }

  parsePluginStatuses(response: PluginStatusResponse): PluginInstallStatus[] {
    const dismissedPluginStatuses = this.pluginStatuses.filter((it) => {
      return it.isDismissed;
    });
    return response.data
      .map((resource) => {
        const pluginInstallId = resource.relationships['plugin-install'].data.id;
        let isDismissed = false;
        // early return if required relationships are missing
        if (dismissedPluginStatuses.some((it) => it.installId === pluginInstallId)) {
          isDismissed = true;
        }

        return {
          installId: resource.relationships['plugin-install'].data.id,
          locationId: resource.relationships['location'].data.id,
          zoneId: null,
          currentStatus: {
            status: this.mapStatus(resource.attributes['current-status']),
            lastSuccessfulEvent:
              'last-successful-event' in resource.attributes ? resource.attributes['last-successful-event'] : null,
          },
          isDismissed: isDismissed,
          integrationName: resource.attributes['integration-name'],
        };
      })
      .filter((it) => it !== null) as PluginInstallStatus[];
  }

  @notEmpty('query') showClearButton!: boolean;
  @and('page', 'loadAccessEvents.isIdle', 'mutateQuery.isIdle') showCount!: boolean;
  @reads('currentLocation.timezone', 'America/Los_Angeles') timezone!: string;

  @action
  formatTimestamp(timestamp?: string | null): string {
    if (!timestamp) return 'No event recorded';

    const date = new Date(timestamp);
    return date.getTime() ? format(date, "MMMM do, 'at' h:mma") : 'Invalid timestamp';
  }

  @action
  dismissPluginInstallStatusNotification(pluginInstallId: string): void {
    const pluginStatus = this.pluginStatuses.find((it) => it.installId === pluginInstallId);
    // set the plugin status to dismissed
    if (pluginStatus) {
      pluginStatus.isDismissed = true;
    }
    this.pluginStatuses = [...this.pluginStatuses];
  }

  get minSearchLength(): number {
    return MIN_SEARCH_LENGTH;
  }

  get todayAsString(): string {
    return formatInTimeZone(new Date(), this.timezone, DATE_FNS_YYYY_MM_DD);
  }

  get todayAsDate(): Date {
    return parseYyyyMmDdInTimeZone(this.todayAsString, new Date(), this.timezone);
  }

  get isAfterToday(): boolean {
    const { startDateWithDefault, endDateWithDefault } = this;

    return isFuture(startDateWithDefault) && isFuture(endDateWithDefault);
  }

  get currentQuery(): string {
    // If a search query typed in, use it.
    // Fallback to query param.
    const { inputQuery, query } = this;

    return isNone(inputQuery) ? query : inputQuery;
  }

  get dateRangeFilterOptions(): string[] {
    return ['Today', 'Yesterday', 'Past 7 days', 'Past 14 days', 'Past 30 days', 'Custom range'];
  }

  get dateWithDefault(): string {
    return this.date || formatInTimeZone(new Date(), this.timezone, DATE_FNS_YYYY_MM_DD);
  }

  get startDateWithDefault(): Date {
    if (this.startDate) return new Date(this.startDate);

    const { dateWithDefault } = this;

    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);
    const start = modifyDateInTimeZone(date, this.timezone, startOfDay);

    return start;
  }

  get endDateWithDefault(): Date {
    if (this.endDate) return new Date(this.endDate);

    const { dateWithDefault } = this;
    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);
    const end = modifyDateInTimeZone(date, this.timezone, endOfDay);

    return end;
  }

  get startTimeWithDefault(): Date {
    if (this.startTime) return new Date(this.startTime);

    const { dateWithDefault } = this;

    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);
    const start = modifyDateInTimeZone(date, this.timezone, startOfDay);

    return start;
  }

  get endTimeWithDefault(): Date {
    if (this.endTime) return new Date(this.endTime);

    const { dateWithDefault } = this;
    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);
    const end = modifyDateInTimeZone(date, this.timezone, endOfDay);

    return end;
  }

  get selectedDateRangeWithDefault(): string {
    return this.selectedDateRange || 'Custom range';
  }

  @action
  didSelectDateRange(startDate: Date, endDate: Date): void {
    this.workplaceMetrics.trackEvent('ACCESS_EVENT_LOG_DATE_RANGE_SELECT_CLICKED');

    // Preserve user-selected times if they exist
    if (this.startTime) {
      const startTimeDate = new Date(this.startTime);
      startDate.setHours(startTimeDate.getHours(), startTimeDate.getMinutes(), 0, 0);
    }
    if (this.endTime) {
      const endTimeDate = new Date(this.endTime);
      endDate.setHours(endTimeDate.getHours(), endTimeDate.getMinutes(), 0, 0);
    }

    this.startDate = startDate;
    this.endDate = endDate;
    this.selectedDateRange = 'Custom range';
    this.date = '';
  }

  get startDateFormat(): string {
    const { timezone, startDateWithDefault } = this;

    return formatInTimeZone(startDateWithDefault, timezone, 'MM/dd/yyyy');
  }

  get endDateFormat(): string {
    const { timezone, endDateWithDefault } = this;

    return formatInTimeZone(endDateWithDefault, timezone, 'MM/dd/yyyy');
  }

  @action
  setStartTime(date: Date): void {
    this.startTime = date;
  }

  @action
  setEndTime(date: Date): void {
    this.endTime = date;
  }

  @action
  setStartDate(date: Date): void {
    this.startDate = date;
    this.reset = true;
  }

  @action
  setEndDate(date: Date): void {
    this.endDate = date;
    this.reset = true;
  }

  @action
  didSelectTimeRange(startTime: Date, endTime: Date): void {
    this.workplaceMetrics.trackEvent('ACCESS_EVENT_LOG_TIME_RANGE_SELECT_CLICKED');
    this.startTime = startTime;
    this.endTime = endTime;
    this.selectedDateRange = 'Custom range';
    this.date = '';
  }

  @action
  setStartAndEndTime(): void {
    this.startTime = this.startTimeWithDefault;
    this.endTime = this.endTimeWithDefault;
    this.startDate = this.startDateWithDefault;
    this.endDate = this.endDateWithDefault;
  }

  @action
  initializeReset(): void {
    this.reset = false;
  }

  get allAccessEvents(): AccessEventModel[] {
    return this.model.accessEvents;
  }

  get relevantAccessEvents(): AccessEventModel[] {
    // eslint-disable-next-line ember/no-get
    return get(this.model, 'accessEvents');
  }

  get primaryHeaders(): SortableField[] {
    return PRIMARY_HEADERS;
  }

  get fieldOptions(): Field[] {
    const alwaysEnabledColumns = ['Name', 'Event Time', 'Event Type'];
    const primaryHeaders = this.primaryHeaders;
    let headers: SortableField[] = [...primaryHeaders];
    headers = headers.map(({ name, componentName, sort, tooltip, tooltipPosition }) => {
      return {
        name,
        componentName,
        // always show alwaysEnabledColumns
        show:
          alwaysEnabledColumns.includes(name) ||
          isPresent(
            (
              this.dashboardFieldsByLocationId[this.currentLocation.location.id] ??
              this.currentLocation.location.dashboardFields
            ).find((field) => field.name === name),
          ),
        // do not allow disabling of alwaysEnabledColumns
        disabled: alwaysEnabledColumns.includes(name),
        sort,
        tooltip,
        tooltipPosition,
      };
    });
    return headers;
  }

  @action
  updateInputQuery(employeeName: string): void {
    this.workplaceMetrics.trackEvent('ACCESS_EVENT_LOG_SEARCHED_EMPLOYEES');

    // Set input value immediately.
    // This reduces risk of rendered input reverting to a previous value.
    this.inputQuery = employeeName;
    if (employeeName) {
      void this.mutateQuery.perform(this.inputQuery);
    } else {
      this.clearSearch();
    }
  }

  mutateQuery = restartableTask(async (employeeName: string) => {
    this.reset = false;
    if (this.query === employeeName) return;

    // Stop if the query is "too short"
    if (employeeName.length < this.minSearchLength) return;

    await timeout(zft(500));

    this.query = employeeName;

    // resetting this to 0 will trigger a new fetch
    this.reset = true;
    this.totalAccessEvents = null;

    this.metrics.trackEvent('Access Events Searched', { search_value: this.query });
  });

  get undismissedPluginStatuses(): PluginInstallStatus[] {
    return this.pluginStatuses.filter((it) => {
      return (
        !it.isDismissed &&
        (it.currentStatus.status === Status.FAILURE ||
          it.currentStatus.status === Status.WARNING ||
          it.currentStatus.status === Status.UNKNOWN ||
          it.currentStatus.status === Status.MISSING)
      );
    });
  }

  loadPluginStatuses = dropTask(async () => {
    const url = urlBuilder.pluginStatusesUrl();
    const settings = {
      accept: 'application/vnd.api+json',
      data: { filter: { 'location-ids': this.currentLocation.location.id } },
    };
    const response = await this.ajax.request<PluginStatusResponse>(url, settings);
    this.pluginStatuses = this.parsePluginStatuses(response);

    // fetch plugin-installs if not in store
    const installIds = this.pluginStatuses.map((status) => status.installId);
    const pluginInstalls = this.store.peekAll('plugin-install');
    const shouldFetch = installIds.some((installId) => !pluginInstalls.findBy('id', installId));
    if (shouldFetch) {
      await this.store.query('plugin-install', { filter: { location: this.currentLocation.location.id } });
    }
  });

  @action
  onDateRangeSelect(option: string): void {
    this.workplaceMetrics.trackEvent('ACCESS_EVENT_LOG_DATE_RANGE_SELECT_CLICKED');

    if (option === 'Custom range') {
      this.showCalendar = true;
      return;
    }

    const now = new Date();
    let start: Date;
    let end: Date = endOfDay(now);

    switch (option) {
      case 'Today':
        start = startOfDay(now);
        break;
      case 'Yesterday':
        start = startOfDay(subDays(now, 1));
        end = endOfDay(subDays(now, 1));
        break;
      case 'Past 7 days':
        start = startOfDay(subDays(now, 7));
        break;
      case 'Past 14 days':
        start = startOfDay(subDays(now, 14));
        break;
      case 'Past 30 days':
        start = startOfDay(subDays(now, 30));
        break;
      default:
        return;
    }

    this.date = '';
    this.selectedDateRange = option;
    this.startDate = addMinutes(start, this.state.minutesBetweenTimezones(start));
    this.endDate = addMinutes(end, this.state.minutesBetweenTimezones(end));
    this.startTime = null;
    this.endTime = null;
    this.reset = true;
  }

  @action
  toggleDashboardField({ name, componentName, show }: Field & { show: boolean }): void {
    let dashboardFields = this.dashboardFieldsByLocationId[this.currentLocation.location.id] ?? [];

    if (show) {
      dashboardFields = [...dashboardFields, { name, componentName }];
    } else {
      dashboardFields = dashboardFields.filter((field) => field.name !== name);
    }

    const locationId = this.currentLocation.location.id;

    this.dashboardFieldsByLocationId = {
      ...this.dashboardFieldsByLocationId,
      [locationId]: dashboardFields,
    };

    saveColumnPreferences(
      this.localStorage,
      'workplace_access_events_column_preferences_location',
      this.currentAdmin?.id,
      locationId,
      dashboardFields,
    );
  }

  @action
  clearSearch(): void {
    this.reset = true;
    this.inputQuery = null;
    this.query = '';
  }

  @action
  setCalendarRef(element: HTMLElement): void {
    this.calendarRef = element;
  }

  @action
  handleBlur(event: FocusEvent): void {
    if (this.calendarRef && !this.calendarRef.contains(event.relatedTarget as Node)) {
      this.showCalendar = false;
    }
  }

  @action
  toggleCalendar(): void {
    this.showCalendar = !this.showCalendar;
  }

  @action
  handleClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    // Don't close if clicking on the time picker dropdowns
    if (target.closest('.powerSelectDropdown--access-event-log-time-picker')) {
      return;
    }
    this.showCalendar = false;
  }

  @action
  selectActivity(activity: FilterItem): void {
    this.currentActivity = activity.filter;
    this.reset = true;
  }

  get activityTypes(): FilterItem[] {
    return ACTIVITY_TYPES;
  }

  get selectedActivityType(): FilterItem | undefined {
    const filter = this.currentActivity;
    const filterOptions = this.activityTypes;
    const selectedOption = filterOptions.find((item) => item.filter == filter);
    return selectedOption || filterOptions[0];
  }

  get outcomeFilters(): FilterItem[] {
    return OUTCOME_FILTERS;
  }

  get availableIntegrationNames(): string[] {
    return [...this._observedIntegrationNames].sort();
  }

  @action
  observeIntegrationNames(names: string[]): void {
    const merged = new Set([...this._observedIntegrationNames, ...names]);
    // Only trigger a re-render if new names were actually added
    if (merged.size !== this._observedIntegrationNames.size) {
      this._observedIntegrationNames = merged;
    }
  }

  @action
  selectOutcomes(outcomes: FilterItem[]): void {
    this.selectedOutcomes = outcomes;
    this.reset = true;
  }

  @action
  selectIntegrationNames(names: string[]): void {
    this.selectedIntegrationNames = names;
    this.reset = true;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'workplace.access-events': WorkplaceAccessEventsController;
  }
}
